import { Button, Card, FormControl, FormErrorMessage } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { isEmpty } from "lodash";
import { FunctionComponent, useMemo } from "react";
import { Question } from "src/components/Inputs/QuestionDynamicInputs/Question";
import { CustomQuestionAnswer } from "src/schemas/CustomQuestionType";
import * as AF from "src/types/formTemplate";
import { useCustomQuestionContext } from "../context/CustomQuestionContext";
import {
  formatCustomQuestionTypeFieldAsProperties,
  IDENTIFYING_CONTACT_FIELDS,
} from "../helpers";

type CustomQuestionTypeFormProps = {
  fieldQuestions: AF.ClonedQuestion<AF.WithId>[];
  isEditingAnswer: boolean;
  isSubmitting: boolean;
  isValid: boolean;
};

/**
 * This is a pseudo nested HTML form. Actual nested form elements are disallowed.
 */
export const AnswerBankEntryFormFields: FunctionComponent<
  CustomQuestionTypeFormProps
> = (props) => {
  const { fieldQuestions, isEditingAnswer, isSubmitting, isValid } = props;
  const { customQuestion, formId } = useCustomQuestionContext();
  const { errors, touched } = useFormikContext<CustomQuestionAnswer>();

  const customQuestionTypeFieldProps = useMemo(() => {
    return formatCustomQuestionTypeFieldAsProperties({
      applicant: { first_name: null, birth_date: null },
      fieldQuestions,
      formId,
      disableDocumentLibrary: false,
    });
  }, [fieldQuestions, formId]);

  const showValidationErrors = !isEmpty(errors) && !isEmpty(touched);
  const memoizedButtonText = useMemo(() => {
    /** Note: this text have to be memoized, otherwise, weglot translation will blow up
     *  when form is submitting due to the button text is replaced by loading spinner.
     *  This issue can be avoided by memoizing the button text, and I think it works because
     *  we avoid re-render of the button.
     */
    return `Save ${customQuestion.question.toLocaleLowerCase()}`;
  }, [customQuestion.question]);

  return (
    <FormControl isInvalid={showValidationErrors}>
      <Card
        padding={4}
        gap={4}
        border={showValidationErrors ? "2px solid red" : "2px solid white"}
      >
        {customQuestionTypeFieldProps.map((fieldProps) => {
          return (
            <Question
              key={fieldProps.id}
              {...fieldProps}
              isDisabled={
                IDENTIFYING_CONTACT_FIELDS.includes(fieldProps.key ?? "") &&
                isEditingAnswer
              }
            />
          );
        })}
        <Button
          type="submit"
          size="sm"
          colorScheme="gray"
          width="100%"
          isLoading={isSubmitting}
          isDisabled={!isValid}
        >
          {memoizedButtonText}
        </Button>
      </Card>
      {showValidationErrors && (
        <FormErrorMessage>This information is required.</FormErrorMessage>
      )}
    </FormControl>
  );
};
